import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { selectCandidateApplication } from 'api/candidate-application-slice';

/**
 * Hook for handling reset of subsequent sections when a previously completed section is modified
 * @param {string} sectionKey - The current section code
 * @param {object} check_values - The check values object containing system_values
 * @returns {function} A function that builds system values with reset logic for subsequent sections
 */
export default function useResetSubsequentSections(sectionKey, check_values) {
  const { candidateFields } = useSelector(selectCandidateApplication);

  const buildSystemValuesWithReset = useCallback(
    (statusKey, newStatus) => {
      // Ensure system_values exists, default to empty object if null/undefined
      const systemValues = check_values?.system_values || {};

      // Get the previous status (will be undefined if key doesn't exist, which is fine)
      // If undefined, wasCompleted will be false, so we won't reset subsequent sections
      const previousStatus = systemValues[statusKey];
      const wasCompleted = previousStatus === 'completed';

      // Build system values with current section status
      const updatedSystemValues = {
        ...systemValues,
        [statusKey]: newStatus,
      };

      // If this section was previously completed and user is making changes,
      // reset all subsequent sections to 'in-progress'
      if (wasCompleted) {
        const sections = candidateFields?.sections || [];
        const currentSectionIndex = sections.findIndex(s => s.code === sectionKey);

        if (currentSectionIndex !== -1) {
          // Reset all sections that come after this one to 'in-progress'
          sections.slice(currentSectionIndex + 1).forEach(subsequentSection => {
            if (subsequentSection.code !== 'consents') {
              const subsequentStatusKey = `${subsequentSection.code}_status`;
              const subsequentStatus = systemValues[subsequentStatusKey];
              // Reset if it was completed (so user needs to review it again)
              if (subsequentStatus === 'completed') {
                updatedSystemValues[subsequentStatusKey] = 'in-progress';
              }
            }
          });
        }
      }

      return updatedSystemValues;
    },
    [sectionKey, check_values, candidateFields],
  );

  return { buildSystemValuesWithReset };
}
